import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toChunks } from '@diana/shared'
import { api, type LessonDetail } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column } from '../../classroom/Shell.js'
import { Bar, Button, ErrorLine, Skeleton } from '../../classroom/ui.js'
import { Icon } from '../../classroom/icons.js'
import { titleFor } from './CoursePage.js'
import { shellCopy } from './copyShell.js'
import { studentCopy } from './copy.js'
import { PlayerContext, StackContext, type ExerciseMemory, type PartState, type PlayerApi, type StackApi } from './player/context.js'
import { playerCopy } from './player/copy.js'
import { ExerciseStep } from './player/ExerciseStep.js'
import { Blocks } from './player/blocks.js'
import { buildSteps, HOMEWORK_KEY, sectionLabelPl } from './player/steps.js'
import { PolishKeys, usePolishKeys } from './player/PolishKeys.js'
import { PartBoundary } from './PartBoundary.js'

/**
 * A lesson's homework (LessonHomework.png): section 7 of the lesson file,
 * its parts stacked on one page, marked by the machine the moment they are
 * checked, writing compared with a model answer. No deadline, nothing sent
 * to Diana — the brief's decision.
 */
export function LessonHomeworkPage() {
  const { slug = '', lessonSlug = '' } = useParams()
  const locale = useLocale()
  const t = studentCopy(locale)
  const lesson = useQuery({
    queryKey: ['course', slug, 'lesson', lessonSlug],
    queryFn: () => api.course.lesson(slug, lessonSlug),
  })
  const course = useQuery({ queryKey: ['course', slug], queryFn: () => api.course.detail(slug) })

  if (lesson.isPending) {
    return (
      <Column>
        <div className="mx-auto w-full max-w-[680px] px-5 py-12 desk:px-12">
          <Skeleton lines={6} />
        </div>
      </Column>
    )
  }
  if (lesson.isError || !lesson.data) {
    return (
      <Column>
        <div className="mx-auto w-full max-w-[680px] px-5 py-12 desk:px-12">
          <ErrorLine>{t.errors.load}</ErrorLine>
        </div>
      </Column>
    )
  }
  return <Homework lesson={lesson.data} slug={slug} courseTitle={course.data?.title ?? ''} />
}

function Homework({ lesson, slug, courseTitle }: { lesson: LessonDetail; slug: string; courseTitle: string }) {
  const locale = useLocale()
  const navigate = useNavigate()
  const s = shellCopy(locale)
  const h = s.homework
  const pt = playerCopy(locale)
  const t = studentCopy(locale)

  const parts = lesson.exercises.filter((e) => e.sectionKey === HOMEWORK_KEY)
  const counted = parts.filter((e) => e.type !== 'unknown')
  const section = lesson.sections.find((x) => x.key === HOMEWORK_KEY)
  const intro = useMemo(() => (section ? toChunks(section.body).flatMap((c) => c.blocks) : []), [section])

  /* ── the parts report up; the one primary checks them all ─────────── */
  const [states, setStates] = useState<Record<string, PartState>>({})
  const checks = useRef(new Map<string, () => void>())
  const stack: StackApi = useMemo(
    () => ({
      register: (id, check) => {
        checks.current.set(id, check)
        return () => checks.current.delete(id)
      },
      report: (id, state) =>
        setStates((prev) => {
          const old = prev[id]
          if (
            old &&
            old.answered === state.answered &&
            old.checked === state.checked &&
            old.done === state.done &&
            old.score === state.score &&
            old.max === state.max
          )
            return prev
          return { ...prev, [id]: state }
        }),
    }),
    [],
  )
  const memory = useRef(new Map<string, ExerciseMemory>()).current
  const noop = useCallback(() => {}, [])
  const player: PlayerApi = useMemo(
    () => ({ locale, t: pt, next: noop, goTo: noop, setPrimary: noop, setStrip: noop, setCounter: noop, setBack: noop, memory }),
    [locale, pt, noop, memory],
  )

  const done = counted.filter((e) => states[e.id]?.done).length
  const checkable = parts.some((e) => states[e.id]?.answered && !states[e.id]?.checked)
  const allDone = counted.length > 0 && done === counted.length

  const scope = useRef<HTMLDivElement>(null)
  const keys = usePolishKeys(scope)

  /* ── the lesson's own outline, for the way back ────────────────────── */
  const steps = useMemo(() => buildSteps(lesson, locale), [lesson, locale])
  const sectionRows = lesson.sections
    .filter((x) => x.key !== HOMEWORK_KEY && x.kind !== 'homework')
    .map((x, i) => {
      const first = steps.findIndex((st) => st.sectionKey === x.key)
      const gradable = lesson.exercises.filter((e) => e.sectionKey === x.key && e.autoGradable)
      const finished = gradable.length > 0 && gradable.every((e) => e.attempt)
      return { key: x.key, n: i + 1, label: sectionLabelPl(x), to: `/panel/kurs/${slug}/${lesson.slug}?krok=${Math.max(0, first) + 1}`, finished }
    })
  const lessonPct = (() => {
    const gradable = lesson.exercises.filter((e) => e.sectionKey !== HOMEWORK_KEY && e.autoGradable)
    return gradable.length ? (gradable.filter((e) => e.attempt?.correct).length / gradable.length) * 100 : 0
  })()

  return (
    <PlayerContext.Provider value={player}>
      <StackContext.Provider value={stack}>
        <aside className="cl-scroll hidden h-full w-[264px] shrink-0 flex-col gap-6 overflow-y-auto border-r border-class-line bg-class-white px-5 py-7 desk:flex">
          <Link to={`/panel/kurs/${slug}`} className="cl-link-quiet !font-normal text-class-gloss">
            ‹ {courseTitle || s.course.myCourses}
          </Link>
          <div className="flex flex-col gap-2.5">
            <p className="m-0 text-class-label font-semibold text-class-ink-faint">{t.course.lesson} {lesson.number}</p>
            <p lang="pl" className="cl-subheading m-0">
              {lesson.title}
            </p>
            <Bar value={lessonPct} className="mt-1 !h-1" />
          </div>
          <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
            {sectionRows.map((r) => (
              <li key={r.key}>
                <Link to={r.to} className="flex min-h-11 items-center gap-3 rounded-class-sm px-2.5 !text-class-ink-soft hover:bg-class-field-soft">
                  <span
                    className={[
                      'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-class-label',
                      r.finished ? 'text-class-ink-faint shadow-[inset_0_0_0_1px_var(--color-class-sky-200)]' : 'bg-class-field-soft text-class-ink-faint',
                    ].join(' ')}
                  >
                    {r.finished ? '✓' : r.n}
                  </span>
                  <span lang="pl">{r.label}</span>
                </Link>
              </li>
            ))}
            <li className="mt-2.5 border-t border-class-line pt-2.5">
              <span
                aria-current="page"
                className="flex min-h-11 items-center gap-3 rounded-class-sm bg-class-field-soft px-2.5 font-semibold text-class-sky-700"
              >
                <Icon name="homework" size={20} />
                {pt.homework}
              </span>
            </li>
          </ol>
        </aside>

        <Column
          top={
            <>
              <p className="m-0 font-semibold">{h.lessonTop(lesson.number)}</p>
              <span className="ml-auto text-class-label text-class-ink-faint">{h.noDeadline}</span>
            </>
          }
          bottom={
            <div className="flex w-full flex-col gap-2">
              {keys.target && <PolishKeys onKey={keys.insert} label={pt.ex.letters} />}
              <div className="flex w-full items-center gap-4">
                <Link to={`/panel/kurs/${slug}/${lesson.slug}`} className="cl-link-quiet hidden whitespace-nowrap desk:inline">
                  ‹ {h.backToLesson}
                </Link>
                <span className="flex-1 text-class-gloss text-class-ink-soft desk:text-center">{h.progress(done, counted.length)}</span>
                {allDone && !checkable ? (
                  <Button onClick={() => navigate('/panel/zadania')}>{h.finished}</Button>
                ) : (
                  <Button disabled={!checkable} onClick={() => checks.current.forEach((check) => check())}>
                    {h.check}
                  </Button>
                )}
              </div>
            </div>
          }
        >
          <div ref={scope}>
            <main className="mx-auto flex max-w-[680px] flex-col gap-11 px-5 pt-8 pb-12 desk:px-12 desk:pt-14 desk:pb-[72px]">
              <section className="flex flex-col gap-1.5">
                <p className="m-0 text-class-label font-semibold text-class-ink-faint">
                  {t.course.lesson} {lesson.number} · <span lang="pl">{lesson.title}</span>
                  {titleFor(lesson, locale) !== lesson.title && ` · ${titleFor(lesson, locale)}`}
                </p>
                <h1 lang="pl" className="cl-title m-0">
                  {h.heading}
                </h1>
                <p className="m-0 text-class-ink-soft">{h.intro(counted.length)}</p>
              </section>

              {intro.length > 0 && (
                <div className="text-class-ink-soft">
                  <Blocks blocks={intro} glosses={[]} locale={locale} t={pt} />
                </div>
              )}

              {parts.map((e, i) => {
                const st = states[e.id]
                const own = e.prompt[locale] || e.prompt.pl
                const instruction = [e.prompt.pl, locale !== 'pl' && own !== e.prompt.pl ? own : ''].filter(Boolean).join(' · ')
                return (
                  <section key={e.id} className="flex flex-col gap-2.5 border-t border-class-line pt-10">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-class-field-soft text-class-label font-semibold text-class-sky-700">
                        {i + 1}
                      </span>
                      <h2 lang="pl" className="cl-subheading m-0">
                        {e.title}
                      </h2>
                      {st?.done && (
                        <span className="ml-auto whitespace-nowrap text-class-gloss text-class-ink-faint">
                          {st.max > 0 ? h.score(st.score, st.max) : h.doneNoScore}
                        </span>
                      )}
                    </div>
                    {instruction && <p className="m-0 mb-1.5 text-class-gloss text-class-ink-faint">{instruction}</p>}
                    <PartBoundary resetKey={e.id} fallback={<ErrorLine>{pt.error.step}</ErrorLine>}>
                      <ExerciseStep exercise={e} figures={lesson.figures.filter((f) => f.exerciseRef === e.ref)} bare />
                    </PartBoundary>
                  </section>
                )
              })}

              {allDone && (
                <section className="flex flex-col items-center gap-2 border-t border-class-line pt-10 text-center">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-class-correct-100 text-class-heading text-class-correct">
                    ✓
                  </span>
                  <p className="cl-subheading m-0">{h.allDone}</p>
                  <Link to="/panel/zadania" className="font-semibold">
                    {h.toList} ›
                  </Link>
                </section>
              )}
            </main>
          </div>
        </Column>
      </StackContext.Provider>
    </PlayerContext.Provider>
  )
}
